import { z } from 'zod';

/**
 * Zod-schema's voor álles wat de app van buiten binnenkrijgt (contract
 * `docs/feature/api-contract-bezorger-fase5.md` §2, §4 en §5). De payload landt in
 * IndexedDB en voedt `tel:`- en `geo:`-links, dus hij wordt aan de grens gevalideerd en
 * niet zomaar vertrouwd. `.strict()` ontbreekt bewust: een additief serverveld mag de
 * bezorger 's avonds niet blokkeren — onbekende velden worden gewoon weggelaten.
 */

const isoUtc = z.string().min(20).max(40);
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const routeStatusSchema = z.enum(['open', 'planned', 'inProgress', 'completed']);

export const stopStatusSchema = z.enum([
  'planned',
  'enRoute',
  'arrived',
  'delivered',
  'failed',
  'redeliveryPlanned',
  'cancelled',
]);

export const parcelStatusSchema = z.enum([
  'created',
  'labelGenerated',
  'readyForPickup',
  'pickedUp',
  'inTransit',
  'delivered',
  'failed',
  'redeliveryPlanned',
  'cancelled',
]);

export const driverParcelSchema = z.object({
  parcelNumber: z.string().min(1).max(40),
  routeStopId: z.string().min(1).max(64),
  routeStopOrder: z.number().int(),
  status: parcelStatusSchema,
  loaded: z.boolean(),
  notTaken: z.boolean(),
  earlyPickup: z.boolean(),
});

export const driverStopSchema = z.object({
  id: z.string().min(1).max(64),
  stopOrder: z.number().int().min(1),
  status: stopStatusSchema,
  isProvisional: z.boolean(),
  partnerName: z.string().max(200),
  phone: z.string().max(40).nullable(),
  street: z.string().max(200),
  postalCodeCity: z.string().max(200),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  plannedArrivalAt: isoUtc.nullable(),
  arrivedAt: isoUtc.nullable(),
  deliveredAt: isoUtc.nullable(),
  failureReason: z.string().max(500).nullable(),
  /** Sleutel bij `failureReason` (amendement A-02). `notTaken` is uitsluitend uitkomst. */
  failureCode: z.string().max(30).nullable().optional(),
  invoiceAnyway: z.boolean(),
  redeliveryPlannedFor: localDate.nullable(),
  parcels: z.array(driverParcelSchema).max(200),
});

export const driverRouteSchema = z.object({
  id: z.string().min(1).max(64),
  routeDate: localDate,
  sequenceNumber: z.number().int(),
  status: routeStatusSchema,
  plannedStartAt: isoUtc.nullable(),
  plannedEndAt: isoUtc.nullable(),
  plannedDistanceMeters: z.number().nullable(),
  plannedDurationSeconds: z.number().nullable(),
  closedAt: isoUtc.nullable(),
  actualStartAt: isoUtc.nullable(),
  actualEndAt: isoUtc.nullable(),
  isProvisional: z.boolean(),
  stopCount: z.number().int().min(0),
  parcelCount: z.number().int().min(0),
  returnLegDurationSeconds: z.number().nullable(),
  returnLegDistanceMeters: z.number().nullable(),
});

export const pickupPointSchema = z.object({
  name: z.string().max(200),
  addressLine: z.string().max(200),
  postalCodeCity: z.string().max(200),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
});

export const carSchema = z.object({
  brand: z.string().max(100).nullable(),
  model: z.string().max(100).nullable(),
  displayName: z.string().max(200),
  licensePlate: z.string().max(20),
});

export const driverRouteTodaySchema = z.object({
  fetchedAt: isoUtc,
  route: driverRouteSchema,
  pickupPoint: pickupPointSchema,
  car: carSchema.nullable(),
  stops: z.array(driverStopSchema).max(200),
});

export const routeProgressSchema = z.object({
  id: z.string().min(1).max(64),
  status: routeStatusSchema,
  closedAt: isoUtc.nullable(),
  actualStartAt: isoUtc.nullable(),
  actualEndAt: isoUtc.nullable(),
  deliveredStopCount: z.number().int().min(0),
  failedStopCount: z.number().int().min(0),
  openStopCount: z.number().int().min(0),
});

export const driverActionResultSchema = z.object({
  clientActionId: z.string().min(1).max(64),
  type: z.string().max(40),
  outcome: z.string().max(60),
  replayed: z.boolean(),
  processedAt: isoUtc,
  /**
   * Amendement A-01 (D135): een `occurredAt` buiten het venster wordt geklemd en gemarkeerd,
   * niet geweigerd. Geen fout — de handeling is geslaagd en de app toont er niets over.
   */
  clockAdjusted: z.boolean().optional(),
  effectiveOccurredAt: isoUtc.nullable().optional(),
  clockSkewSeconds: z.number().nullable().optional(),
  /** Amendement A-04: de daadwerkelijk geplande herbezorgdag bij `stop.fail`. */
  redeliveryPlannedFor: localDate.nullable().optional(),
  route: routeProgressSchema.nullable().optional(),
  stop: driverStopSchema.nullable().optional(),
  parcel: driverParcelSchema.nullable().optional(),
});

export const authUserSchema = z.object({
  id: z.string().max(64),
  email: z.string().max(200),
  firstName: z.string().max(100).nullable().optional(),
  lastName: z.string().max(100).nullable().optional(),
  emailVerified: z.boolean().optional(),
  roles: z.array(z.string().max(40)).max(20),
});

export const authTokensSchema = z.object({
  accessToken: z.string().min(1),
  accessTokenExpiresAt: isoUtc,
  refreshToken: z.string().min(1),
  refreshTokenExpiresAt: isoUtc,
  user: authUserSchema,
});

export function apiResponseSchema<T extends z.ZodTypeAny>(data: T) {
  return z.object({
    success: z.boolean(),
    data: data.nullable(),
    message: z.string().nullable().optional(),
    errors: z.array(z.string().max(200)).max(50).nullable().optional(),
  });
}
